import { useState, useCallback } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { formatFiat, getCurrencySymbol, calcTax } from '@/lib/bch';
import {
  InvoiceLineItem, StoredInvoice,
  buildInvoiceUrl, addInvoice, loadInvoices, markInvoicePaid, deleteInvoice,
} from '@/lib/invoice';
import type { InvoiceData } from '@/lib/invoice';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';

function formatDate(ts: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Invoice form ────────────────────────────────────────────────────────────

function InvoiceForm({ settings, onCreated }: {
  settings: { bchAddress: string; currency: string; merchantName: string; merchantTagline?: string; logoDataUrl?: string; brandColor: string; taxRate: number; taxName: string };
  onCreated: (info: ShareInfo) => void;
}) {
  const sym = getCurrencySymbol(settings.currency);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [items, setItems] = useState<InvoiceLineItem[]>([
    { description: '', qty: 1, price: 0 },
  ]);

  const addItem = () => setItems((prev) => [...prev, { description: '', qty: 1, price: 0 }]);
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const updateItem = (idx: number, field: keyof InvoiceLineItem, val: string | number) => {
    setItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      if (field === 'description') return { ...item, description: val as string };
      if (field === 'qty') return { ...item, qty: Math.max(1, parseInt(val as string) || 1) };
      return { ...item, price: Math.max(0, parseFloat(val as string) || 0) };
    }));
  };

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const { taxAmount, total } = calcTax(subtotal, settings.taxRate);

  const isValid = clientName.trim().length > 0
    && items.some((i) => i.description.trim() && i.price > 0)
    && settings.bchAddress.length > 0;

  const handleCreate = () => {
    if (!isValid) return;

    const validItems = items.filter((i) => i.description.trim() && i.price > 0);
    const id = crypto.randomUUID();
    const dueDateMs = dueDate ? new Date(dueDate).getTime() : 0;

    const invoiceData: InvoiceData = {
      v: 1,
      id,
      merchantName: settings.merchantName,
      merchantTagline: settings.merchantTagline,
      logoDataUrl: settings.logoDataUrl,
      brandColor: settings.brandColor,
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim() || undefined,
      currency: settings.currency,
      items: validItems,
      subtotal: parseFloat(subtotal.toFixed(2)),
      taxRate: settings.taxRate,
      taxName: settings.taxName,
      taxAmount: parseFloat(taxAmount.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      note: note.trim() || undefined,
      address: settings.bchAddress,
      dueDate: dueDateMs,
      createdAt: Date.now(),
    };

    const url = buildInvoiceUrl(invoiceData);

    const stored: StoredInvoice = {
      id,
      clientName: clientName.trim(),
      total: parseFloat(total.toFixed(2)),
      currency: settings.currency,
      createdAt: Date.now(),
      dueDate: dueDateMs,
      url,
      paid: false,
    };
    addInvoice(stored);

    // Record invoice creation for analytics (anonymous, best-effort).
    track({
      type: 'invoice',
      total: stored.total,
      currency: stored.currency,
      itemCount: validItems.length,
    });

    onCreated({
      url,
      merchantName: settings.merchantName,
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim() || undefined,
      total: parseFloat(total.toFixed(2)),
      currency: settings.currency,
    });
  };

  return (
    <div className="space-y-4">
      {/* Client info */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700 space-y-3">
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Bill to</p>
        <input
          type="text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder="Client name *"
          maxLength={100}
          className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
        />
        <input
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          placeholder="Client email (optional)"
          className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
        />
      </div>

      {/* Line items */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Line items</p>
          <button onClick={addItem} className="text-xs font-bold text-[#0AC18E] hover:underline">
            + Add item
          </button>
        </div>

        {items.map((item, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateItem(idx, 'description', e.target.value)}
                placeholder="Description *"
                maxLength={120}
                className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
              />
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 flex-1">
                  <span className="text-xs text-zinc-400 shrink-0">Qty</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    value={item.qty}
                    onChange={(e) => updateItem(idx, 'qty', e.target.value)}
                    className="w-16 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg px-2 py-2 text-sm text-center text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
                  />
                </div>
                <div className="flex items-center gap-1.5 flex-1">
                  <span className="text-xs text-zinc-400 shrink-0">{sym}</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={item.price || ''}
                    onChange={(e) => updateItem(idx, 'price', e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg px-2 py-2 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
                  />
                </div>
              </div>
            </div>
            {items.length > 1 && (
              <button
                onClick={() => removeItem(idx)}
                className="mt-1 w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-400 text-sm font-bold hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center shrink-0"
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {/* Running total */}
        <div className="pt-3 border-t border-dashed border-zinc-200 dark:border-zinc-700 space-y-1">
          <div className="flex justify-between text-sm text-zinc-500">
            <span>Subtotal</span>
            <span>{formatFiat(subtotal, settings.currency)}</span>
          </div>
          {settings.taxRate > 0 && (
            <div className="flex justify-between text-sm text-zinc-500">
              <span>{settings.taxName} ({settings.taxRate}%)</span>
              <span>{formatFiat(taxAmount, settings.currency)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-zinc-900 dark:text-zinc-50 pt-1">
            <span>Total</span>
            <span>{formatFiat(total, settings.currency)}</span>
          </div>
        </div>
      </div>

      {/* Due date + note */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Due date <span className="font-normal normal-case text-zinc-300">(optional)</span>
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Note <span className="font-normal normal-case text-zinc-300">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Payment terms, project details…"
            maxLength={300}
            rows={3}
            className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E] resize-none"
          />
        </div>
      </div>

      {/* Create button */}
      {!settings.bchAddress ? (
        <p className="text-sm text-amber-500 text-center font-medium">⚙️ Set your BCH address in Settings first</p>
      ) : (
        <button
          onClick={handleCreate}
          disabled={!isValid}
          className={cn(
            'w-full py-5 rounded-2xl text-lg font-bold transition-all duration-150 select-none',
            isValid
              ? 'bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 active:scale-98 hover:bg-[#09AF81]'
              : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 dark:text-zinc-500 cursor-not-allowed'
          )}
        >
          Create Invoice →
        </button>
      )}
    </div>
  );
}

// ─── Share screen (after creation) ───────────────────────────────────────────

interface ShareInfo {
  url: string;
  merchantName: string;
  clientName: string;
  clientEmail?: string;
  total: number;
  currency: string;
}

function ShareScreen({ info, onDone }: { info: ShareInfo; onDone: () => void }) {
  const { url, merchantName, clientName, clientEmail, total, currency } = info;
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      prompt('Copy invoice link:', url);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Invoice', text: 'Please pay this invoice', url });
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      } catch {
        await handleCopy();
      }
    } else {
      await handleCopy();
    }
  };

  return (
    <div className="space-y-5">
      {/* Success header */}
      <div className="text-center py-4">
        <div className="text-5xl mb-3">✉️</div>
        <h2 className="text-xl font-black text-zinc-900 dark:text-zinc-50">Invoice Created!</h2>
        <p className="text-sm text-zinc-400 mt-1">Share the link below with your client</p>
      </div>

      {/* Primary action — Share (instant via iMessage, WhatsApp, etc.) */}
      <button
        onClick={handleShare}
        className={cn(
          'w-full py-5 rounded-2xl text-base font-bold transition-all',
          shared
            ? 'bg-[#0AC18E]/15 text-[#0AC18E] border-2 border-[#0AC18E]'
            : 'bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98'
        )}
      >
        {shared ? '✓ Sent!' : '📤 Send to Client — Instant'}
      </button>
      <p className="text-xs text-zinc-400 text-center -mt-3">
        Opens iMessage, WhatsApp, Telegram, or any messaging app
      </p>

      {/* Secondary actions */}
      <div className="flex gap-3">
        <button
          onClick={handleCopy}
          className={cn(
            'flex-1 py-3.5 rounded-2xl text-sm font-bold transition-all',
            copied
              ? 'bg-[#0AC18E]/15 text-[#0AC18E] border-2 border-[#0AC18E]'
              : 'bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border-2 border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/50'
          )}
        >
          {copied ? '✓ Copied!' : '📋 Copy Link'}
        </button>
        {(() => {
          const subject = encodeURIComponent(`Invoice from ${merchantName} — ${formatFiat(total, currency)}`);
          const body = encodeURIComponent(
            `Hi ${clientName},\n\nPlease find your invoice below:\n\n${url}\n\nAmount due: ${formatFiat(total, currency)}\n\nThank you,\n${merchantName}`
          );
          const to = clientEmail ? encodeURIComponent(clientEmail) : '';
          const mailto = `mailto:${to}?subject=${subject}&body=${body}`;
          return (
            <a
              href={mailto}
              className="flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-2xl text-sm font-bold bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border-2 border-zinc-200 dark:border-zinc-700 hover:border-zinc-400 transition-all"
            >
              ✉️ Email
            </a>
          );
        })()}
      </div>

      {/* URL display (collapsible detail) */}
      <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-700">
        <p className="text-xs font-mono text-zinc-500 break-all leading-relaxed line-clamp-3">{url}</p>
      </div>

      {/* Open preview + create another */}
      <div className="flex gap-3">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 text-center py-3 rounded-2xl text-sm font-semibold text-[#0AC18E] bg-[#0AC18E]/10 border border-[#0AC18E]/20 hover:bg-[#0AC18E]/20 transition-colors"
        >
          🔗 Preview
        </a>
        <button
          onClick={onDone}
          className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 active:scale-98 transition-all"
        >
          + New Invoice
        </button>
      </div>
    </div>
  );
}

// ─── Invoice history list ────────────────────────────────────────────────────

function InvoiceHistory({ currency }: { currency: string }) {
  const [invoices, setInvoices] = useState<StoredInvoice[]>(() => loadInvoices());
  const [copied, setCopied] = useState<string | null>(null);

  const refresh = useCallback(() => setInvoices(loadInvoices()), []);

  const handleCopy = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch { /* */ }
  };

  const handleMarkPaid = (id: string) => {
    markInvoicePaid(id);
    refresh();
  };

  const handleDelete = (id: string) => {
    deleteInvoice(id);
    refresh();
  };

  if (invoices.length === 0) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider px-1">Sent Invoices</p>
      {invoices.map((inv) => {
        const isOverdue = inv.dueDate > 0 && inv.dueDate < Date.now() && !inv.paid;
        return (
          <div
            key={inv.id}
            className={cn(
              'bg-white dark:bg-zinc-800 rounded-2xl p-4 border shadow-sm',
              inv.paid ? 'border-[#0AC18E]/30 opacity-70' : isOverdue ? 'border-red-200 dark:border-red-900/40' : 'border-zinc-100 dark:border-zinc-700'
            )}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={cn('text-base font-bold', inv.paid ? 'text-zinc-400 line-through' : 'text-zinc-900 dark:text-zinc-50')}>
                  {formatFiat(inv.total, inv.currency)}
                </span>
                {inv.paid && (
                  <span className="text-[10px] font-bold text-[#0AC18E] bg-[#0AC18E]/10 px-2 py-0.5 rounded-full border border-[#0AC18E]/30">
                    Paid
                  </span>
                )}
                {isOverdue && (
                  <span className="text-[10px] font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full border border-red-200 dark:border-red-800">
                    Overdue
                  </span>
                )}
              </div>
              <span className="text-xs text-zinc-400">{formatDate(inv.createdAt)}</span>
            </div>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 truncate">{inv.clientName}</p>
            {inv.dueDate > 0 && !inv.paid && (
              <p className="text-xs text-zinc-400 mt-0.5">Due {formatDate(inv.dueDate)}</p>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => handleCopy(inv.url, inv.id)}
                className={cn(
                  'text-xs font-semibold px-3 py-1.5 rounded-lg transition-all',
                  copied === inv.id
                    ? 'bg-[#0AC18E]/15 text-[#0AC18E]'
                    : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 hover:text-[#0AC18E]'
                )}
              >
                {copied === inv.id ? '✓ Copied' : '📋 Copy Link'}
              </button>
              <a
                href={inv.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-[#0AC18E] hover:underline"
              >
                Open ↗
              </a>
              {!inv.paid && (
                <button
                  onClick={() => handleMarkPaid(inv.id)}
                  className="text-xs font-semibold text-zinc-400 hover:text-[#0AC18E] transition-colors ml-auto"
                >
                  Mark paid
                </button>
              )}
              <button
                onClick={() => handleDelete(inv.id)}
                className="text-xs font-semibold text-zinc-400 hover:text-red-500 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main view ───────────────────────────────────────────────────────────────

type InvoiceMode = 'create' | 'share';

export function InvoiceView() {
  const { settings } = usePOS();
  const [mode, setMode] = useState<InvoiceMode>('create');
  const [lastInfo, setLastInfo] = useState<ShareInfo | null>(null);

  const handleCreated = (info: ShareInfo) => {
    setLastInfo(info);
    setMode('share');
  };

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Invoices</h2>
          <p className="text-sm text-zinc-400">
            {mode === 'create' ? 'Create & share payment links' : 'Share with your client'}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pb-4">
        {mode === 'create' && (
          <>
            <InvoiceForm settings={settings} onCreated={handleCreated} />
            <InvoiceHistory currency={settings.currency} />
          </>
        )}
        {mode === 'share' && lastInfo && (
          <ShareScreen info={lastInfo} onDone={() => setMode('create')} />
        )}
      </div>
    </div>
  );
}
